import { SidebarInset, SidebarProvider, Toaster } from '@/components/health-ui';
import { createRootRoute, Outlet } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { AppSidebar } from '@/components/app-sidebar';
import { ContentRouteErrorComponent } from '@/components/content-route-error-component';
import { MobileTabBar } from '@/components/mobile-tab-bar';
import { MobileTopBar } from '@/components/mobile-top-bar';

type RouteErrorBoundaryProps = {
  error: unknown;
  reset: () => void;
};

/**
 * The one app shell, shared by the normal and error-boundary routes so the
 * mobile chrome cannot drift between them.
 */
function AppShell({ children }: { children: ReactNode }): React.JSX.Element {
  return (
    <SidebarProvider className="min-h-svh bg-canvas text-foreground">
      <AppSidebar />
      {/* min-w-0 lets the content pane shrink below its children's intrinsic
          width, so wide tables scroll inside their own container instead of
          stretching the whole page.
          The bottom padding clears the fixed tab bar for normal flow content;
          viewport-sticky children are unaffected by it and carry their own
          bottom offset (see profile-tab / coverage-tab). */}
      <SidebarInset className="min-w-0 pb-[var(--mobile-tab-bar-h)] lg:pb-0">
        <MobileTopBar />
        {children}
      </SidebarInset>
      <MobileTabBar />
      <Toaster />
    </SidebarProvider>
  );
}

function RootComponent(): React.JSX.Element {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

function RootErrorComponent({
  error,
  reset,
}: Readonly<RouteErrorBoundaryProps>): React.JSX.Element {
  return (
    <AppShell>
      <ContentRouteErrorComponent error={error} reset={reset} />
    </AppShell>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
  errorComponent: RootErrorComponent,
});
